import { ChangeDetectionStrategy, Component, computed, inject, type Signal } from '@angular/core';
import { MtmShareDialogComponent, type ShareDialogAsset, type ShareDialogResult } from '@mtm/drive/components';
import { DriveShareUiService } from '../../../shared/drive/drive-share-ui.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/**
 * Top-nav Share button: opens a popover with `<mtm-share-dialog>` targeting
 * whatever `DriveShareUiService` currently reports (the drive surface's
 * previewed file, or its open folder when nothing is previewed). Mirrors the
 * playground's `app-header` share-anchor.
 */
@Component({
  selector: 'mtm-top-nav-drive-share-btn',
  standalone: true,
  imports: [MtmShareDialogComponent],
  providers: [TranslatePipe],
  templateUrl: './top-nav-drive-share-btn.component.html',
  styleUrl: './top-nav-drive-share-btn.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopNavDriveShareBtnComponent {
  private readonly translate = inject(TranslatePipe);
  protected readonly shareUi = inject(DriveShareUiService);

  protected readonly serverError: Signal<string | null> = computed(() => {
    const key = this.shareUi.shareError();
    return key ? this.translate.transform(key) : null;
  });

  protected get toggleTitle(): string {
    return this.translate.transform('drive_shareLabel');
  }

  protected onToggle(event: Event): void {
    event.stopPropagation();
    this.shareUi.toggleOpen();
  }

  /**
   * `<mtm-share-dialog>` dismisses on any `document:pointerdown` outside its
   * own host element (`ElementRef` of the `<mtm-share-dialog>` tag). Angular's
   * `(click).stopPropagation()` on the trigger button does not protect clicks
   * *inside* the popover — `pointerdown` is a separate native event with its
   * own propagation path, dispatched to `document` before Angular's `click`
   * handlers run. Stopping it here, on the whole anchor, keeps every pointer
   * interaction inside the popover (including a `submit()` click) from ever
   * reaching that listener.
   */
  protected onAnchorPointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  protected onConfirmed(result: ShareDialogResult): void {
    void this.shareUi.confirm(result);
  }

  protected onCancelled(): void {
    this.shareUi.close();
  }

  protected asset(): ShareDialogAsset | null {
    return this.shareUi.shareAsset();
  }
}
